import { Pressable, SxProp, View } from 'dripsy';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { ChainSelectorDropdownMenu } from './ChainSelectorDropdownMenu';
import { useChainSelector, ChainData } from './useChainSelector';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextSmall } from '@/design-system/components/typography';
import { Icon } from '@/design-system/components/icons/Icon';
import { DigitalDepositAddress } from '../../../../../../packages/utx-access-client/src/react';

interface ChainSelectorProps {
  value?: string;
  sx?: SxProp;
  onValueChange: (value: string) => void;
  onChainChange?: (value: ChainData | undefined) => void;
  chainsDisplay: { chain: string; chainName: string | undefined }[];
}

export const ChainSelector: React.FC<ChainSelectorProps> = ({ value, sx, onValueChange, onChainChange, chainsDisplay }) => {
  const { t } = useTranslation('digital-injection');
  const navigation = useNavigation<any>();
  const layout = useContext(LayoutContext);

  const { hasFocus, selectedChain, selectedChainId, displayChainData, changeFocus, changeSearchText, selectChainId } = useChainSelector({
    value,
    onValueChange,
    onChainChange,
    chainsDisplay
  });

  return (
    <View sx={{ ...styles.container, ...sx }}>
      {/* Button */}
      <Pressable
        onPress={() => {
          changeFocus(true);
        }}>
        {hasFocus ? (
          <UniInput
            hasFocus={hasFocus}
            prefixIcon="search"
            onValueChange={(value) => changeSearchText(value || '')}
            placeholder={t('digital-injection::glossary.select-network')}
            wrapperSx={{ flexGrow: 1 }}
            sx={{ width: '100%', height: '$15' }}
          />
        ) : null}
        {!hasFocus && !selectedChainId ? (
          <View sx={styles.accountButton}>
            <TextBody sx={{ color: '$textPlaceholder' }}>{t('digital-injection::glossary.select-network')}</TextBody>
            <Icon name={layout.isMobile ? 'arrowRight' : 'arrowDown'} />
          </View>
        ) : null}
        {!hasFocus && selectedChainId ? (
          <View sx={styles.accountButton}>
            <View>
              <TextBody label>{selectedChain?.chain}</TextBody>
              <TextSmall secondary>{selectedChain?.chainName}</TextSmall>
            </View>
            <Icon name={layout.isMobile ? 'arrowRight' : 'arrowDown'} />
          </View>
        ) : null}
      </Pressable>
      {/* Dropdown Menu */}
      <ChainSelectorDropdownMenu
        hasFocus={hasFocus}
        displayChainData={displayChainData}
        changeFocus={changeFocus}
        selectChainId={selectChainId}
        onValueChange={onValueChange}
      />
    </View>
  );
};

enum StyleName {
  container = 'container',
  accountButton = 'accountButton',
  dropdownMenu = 'dropdownMenu',
  dropdownItem = 'dropdownItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    zIndex: 10
  },
  accountButton: {
    height: '$15',
    paddingX: '$4',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '$borderField',
    borderRadius: 5
  },
  dropdownMenu: {
    position: 'absolute',
    top: '$15',
    left: 0,
    width: '100%',
    maxHeight: 250,
    paddingY: '$2',
    backgroundColor: '$backgroundPopup',
    borderRadius: 5,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10
  },
  dropdownItem: {
    paddingX: '$3',
    paddingY: '$4',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  }
};
